const TOOLTIP_SELECTOR = '[data-tooltip-block]';
const DESKTOP_POINTER_QUERY = window.matchMedia('(hover: hover) and (pointer: fine)');

let activeTooltip = null;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getTooltipParts(block) {
  return {
    host: block?.parentElement || null,
    trigger: block?.querySelector('[data-tooltip-trigger]') || null,
    panel: block?.querySelector('[data-tooltip-panel]') || null,
    link: block?.querySelector('[data-tooltip-link]') || null,
  };
}

function isDesktopPointerEnvironment() {
  return DESKTOP_POINTER_QUERY.matches;
}

function isDesktopEnabled(block) {
  return block?.dataset.tooltipDesktopEnabled !== 'false';
}

function isMobileEnabled(block) {
  return block?.dataset.tooltipMobileEnabled !== 'false';
}

function hasTooltipLink(block) {
  return block?.dataset.tooltipHasLink === 'true';
}

function setTooltipState(block, isOpen) {
  const { trigger, panel } = getTooltipParts(block);

  if (!block || !trigger || !panel) {
    return;
  }

  block.dataset.tooltipState = isOpen ? 'open' : 'closed';
  trigger.setAttribute('aria-expanded', String(isOpen));
  panel.setAttribute('aria-hidden', String(!isOpen));

  if (!isOpen && activeTooltip === block) {
    activeTooltip = null;
  }
}

function closeTooltip(block, { clearMode = false } = {}) {
  if (!block) {
    return;
  }

  if (clearMode) {
    block.dataset.tooltipMode = '';
  }

  setTooltipState(block, false);
}

function closeAllTooltips(exceptBlock = null) {
  document.querySelectorAll(TOOLTIP_SELECTOR).forEach((block) => {
    if (block !== exceptBlock) {
      closeTooltip(block);
    }
  });
}

function openTooltip(block, mode) {
  const { host, trigger, panel } = getTooltipParts(block);

  if (!block || !host || !trigger || !panel) {
    return false;
  }

  if (activeTooltip && activeTooltip !== block) {
    closeTooltip(activeTooltip);
  }

  block.dataset.tooltipMode = mode;
  setTooltipState(block, true);
  activeTooltip = block;

  return true;
}

function setTooltipPosition(block, x, y) {
  const { host, panel } = getTooltipParts(block);

  if (!host || !panel) {
    return;
  }

  const safeInset = 12;
  const hostRect = host.getBoundingClientRect();
  const panelWidth = panel.offsetWidth || 0;
  const panelHeight = panel.offsetHeight || 0;
  const maxLeft = Math.max(safeInset, hostRect.width - panelWidth - safeInset);
  const maxTop = Math.max(safeInset, hostRect.height - panelHeight - safeInset);
  const nextX = clamp(x, safeInset, maxLeft);
  const nextY = clamp(y, safeInset, maxTop);

  block.style.setProperty('--tooltip-x', `${nextX}px`);
  block.style.setProperty('--tooltip-y', `${nextY}px`);
}

function openPointerTooltip(block, event) {
  if (!isDesktopPointerEnvironment() || !isDesktopEnabled(block)) {
    return;
  }

  if (!openTooltip(block, 'pointer')) {
    return;
  }

  updatePointerTooltip(block, event);
}

function updatePointerTooltip(block, event) {
  const { host } = getTooltipParts(block);

  if (!host) {
    return;
  }

  const hostRect = host.getBoundingClientRect();
  const offset = 16;
  const x = event.clientX - hostRect.left + offset;
  const y = event.clientY - hostRect.top + offset;

  setTooltipPosition(block, x, y);
}

function openFocusTooltip(block) {
  if (!isDesktopEnabled(block)) {
    return;
  }

  const suppressUntil = Number.parseInt(block.dataset.tooltipSuppressFocusUntil || '0', 10);
  if (suppressUntil > Date.now()) {
    return;
  }

  if (!openTooltip(block, 'focus')) {
    return;
  }

  setTooltipPosition(block, 16, 16);
}

function toggleTapTooltip(block) {
  if (!isMobileEnabled(block) || isDesktopPointerEnvironment()) {
    return;
  }

  if (activeTooltip === block && block.dataset.tooltipState === 'open') {
    closeTooltip(block);
    return;
  }

  openTooltip(block, 'tap');
}

function initializeTooltip(block) {
  if (!block || block.dataset.tooltipInitialized === 'true') {
    return;
  }

  const { host, trigger, panel, link } = getTooltipParts(block);
  if (!host || !trigger || !panel) {
    return;
  }

  block.dataset.tooltipInitialized = 'true';
  host.setAttribute('data-tooltip-host', '');
  block.style.setProperty('--tooltip-x', '16px');
  block.style.setProperty('--tooltip-y', '16px');

  trigger.addEventListener('pointerdown', () => {
    block.dataset.tooltipSuppressFocusUntil = String(Date.now() + 300);
  });

  host.addEventListener('pointerenter', (event) => {
    if (event.pointerType === 'mouse' || event.pointerType === 'pen') {
      openPointerTooltip(block, event);
    }
  });

  host.addEventListener('pointermove', (event) => {
    if (
      !isDesktopPointerEnvironment() ||
      block.dataset.tooltipState !== 'open' ||
      block.dataset.tooltipMode !== 'pointer'
    ) {
      return;
    }

    if (event.target instanceof Element && event.target.closest('[data-tooltip-panel]')) {
      return;
    }

    updatePointerTooltip(block, event);
  });

  host.addEventListener('pointerleave', () => {
    if (block.dataset.tooltipMode === 'pointer') {
      closeTooltip(block);
    }
  });

  block.addEventListener('focusin', (event) => {
    if (!isDesktopPointerEnvironment()) {
      return;
    }

    if (event.target === trigger) {
      openFocusTooltip(block);
    }
  });

  block.addEventListener('focusout', (event) => {
    const nextFocused = event.relatedTarget;
    if (nextFocused && block.contains(nextFocused)) {
      return;
    }

    if (block.dataset.tooltipMode === 'focus') {
      closeTooltip(block);
    }
  });

  trigger.addEventListener('click', (event) => {
    if (link && isDesktopPointerEnvironment()) {
      event.preventDefault();
      link.click();
      return;
    }

    if (isDesktopPointerEnvironment()) {
      return;
    }

    if (!isMobileEnabled(block)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (link && hasTooltipLink(block) && block.dataset.tooltipState === 'open') {
      link.click();
      return;
    }

    toggleTapTooltip(block);
  });
}

function initializeTooltips(root = document) {
  root.querySelectorAll(TOOLTIP_SELECTOR).forEach((block) => {
    initializeTooltip(block);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initializeTooltips();
});

document.addEventListener('shopify:section:load', (event) => {
  initializeTooltips(event.target);
});

document.addEventListener('click', (event) => {
  if (!activeTooltip || activeTooltip.dataset.tooltipMode !== 'tap') {
    return;
  }

  if (activeTooltip.contains(event.target)) {
    return;
  }

  closeTooltip(activeTooltip);
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    closeAllTooltips();
  }
});

window.addEventListener('resize', () => {
  closeAllTooltips();
});

if (typeof DESKTOP_POINTER_QUERY.addEventListener === 'function') {
  DESKTOP_POINTER_QUERY.addEventListener('change', () => {
    closeAllTooltips();
  });
} else if (typeof DESKTOP_POINTER_QUERY.addListener === 'function') {
  DESKTOP_POINTER_QUERY.addListener(() => {
    closeAllTooltips();
  });
}
